import {ArrowRightIcon, BookIcon, PencilIcon, PauseIcon, PlayIcon} from '@primer/octicons-react'
import {ActionList, Label, Spinner} from '@primer/react'

import styles from './TriageAgentOverlay.module.css'
import {useTriageAgentStatus, useUpdateTriageAgentStatus} from './use-triage-agent-status'
import {
  CREATE_TRIAGE_AGENT_CONFIGURATION_TEXT,
  CONFIG_FILE_NOT_FOUND_TEXT,
  CONFIG_FILE_FOUND_TEXT,
  PAUSE_AGENT_TEXT,
  RESUME_AGENT_TEXT,
  TRIAGE_AGENT_DOCS_URL,
  TRIAGE_AGENT_PERMISSION_MESSAGE,
  TRIAGE_AGENT_TITLE,
  VIEW_DOCUMENTATION_TEXT,
} from './utils/constants'
import {getTriageAgentConfigUrl, getNewTriageAgentConfigUrl} from './utils/triage-agent-urls'

type TriageAgentOverlayContentProps = {
  repoOwner: string
  repoName: string
  onClose: () => void
  viewerCanConfigureTriageAgent: boolean
}

export function TriageAgentOverlayContent({
  repoOwner,
  repoName,
  onClose,
  viewerCanConfigureTriageAgent,
}: TriageAgentOverlayContentProps) {
  const {data, isLoading, isError: isFetchError} = useTriageAgentStatus(repoOwner, repoName)
  const {updateStatus, isPending: isUpdating} = useUpdateTriageAgentStatus(repoOwner, repoName)

  if (isLoading) {
    return (
      <div className={styles.overlayContainer}>
        <div className={styles.loadingContainer}>
          <Spinner size="medium" />
        </div>
      </div>
    )
  }

  const isError = isFetchError || !data
  if (isError) {
    return (
      <div className={styles.overlayContainer}>
        <div className={styles.header}>
          <span className={styles.title}>{TRIAGE_AGENT_TITLE}</span>
          <Label variant="success">Preview</Label>
        </div>
        <div className={styles.errorMessage}>Failed to load triage agent settings.</div>
      </div>
    )
  }

  const configUrl = getTriageAgentConfigUrl({
    repoOwner,
    repoName,
    defaultBranch: data.default_branch,
    configPath: data.config_path,
  })
  const newConfigUrl = getNewTriageAgentConfigUrl({
    repoOwner,
    repoName,
    defaultBranch: data.default_branch,
  })

  const configExists = data.config_exists
  const isEnabled = data.enabled

  const handlePauseResume = () => {
    updateStatus(!isEnabled)
  }

  const handleConfigureClick = () => {
    onClose()
  }

  return (
    <div className={styles.overlayContainer} data-testid="triage-agent-overlay">
      <div className={styles.header}>
        <span className={styles.title}>{TRIAGE_AGENT_TITLE}</span>
        <Label variant="success">Preview</Label>
      </div>

      <ActionList>
        {/* Start/Stop Agent Option */}
        <ActionList.Item onSelect={handlePauseResume} disabled={isUpdating || !viewerCanConfigureTriageAgent}>
          <ActionList.LeadingVisual>{isEnabled ? <PauseIcon /> : <PlayIcon />}</ActionList.LeadingVisual>
          {isEnabled ? PAUSE_AGENT_TEXT : RESUME_AGENT_TEXT}
          {!viewerCanConfigureTriageAgent && (
            <ActionList.Description variant="block">{TRIAGE_AGENT_PERMISSION_MESSAGE}</ActionList.Description>
          )}
        </ActionList.Item>

        {/* Configure Agent Option */}
        <ActionList.LinkItem href={configExists ? configUrl : newConfigUrl} onClick={handleConfigureClick}>
          <ActionList.LeadingVisual>
            <PencilIcon />
          </ActionList.LeadingVisual>
          {CREATE_TRIAGE_AGENT_CONFIGURATION_TEXT}
          <ActionList.Description variant="block">
            {configExists ? CONFIG_FILE_FOUND_TEXT : CONFIG_FILE_NOT_FOUND_TEXT}
          </ActionList.Description>
          <ActionList.TrailingVisual>
            <ArrowRightIcon />
          </ActionList.TrailingVisual>
        </ActionList.LinkItem>

        <ActionList.Divider />

        <ActionList.LinkItem href={TRIAGE_AGENT_DOCS_URL} target="_blank" rel="noopener noreferrer">
          <ActionList.LeadingVisual>
            <BookIcon />
          </ActionList.LeadingVisual>
          {VIEW_DOCUMENTATION_TEXT}
        </ActionList.LinkItem>
      </ActionList>
    </div>
  )
}
